/**
 * "Prazo de auditoria" da política (spec 2026-10-01 §3.3/§7.4). Helpers puros: o formulário
 * só liga os campos; regra de teto, mensagem e prévia moram aqui para serem testadas.
 */

/** Teto do campo (G2-D4): 1 ano, para limitar o laço hora a hora do backend. */
export const AUDIT_DEADLINE_MAX_HOURS = 8760

/** Texto do mockup `politicaregistro--erro-validacao.png` (o mesmo `400` do backend). */
export const AUDIT_DEADLINE_CALCULATION_REQUIRED_MESSAGE =
  'Escolha o cálculo de horas: obrigatório quando há prazo de auditoria'

export const AUDIT_DEADLINE_HELP =
  'O relógio começa quando o registro entra em auditoria (folha travada, atividade enviada ou upload de App externo / Folha não diária). "Em auditoria" não pausa; reenvio após rejeição reinicia. Vazio = sem prazo (o registro aparece em "Sem SLA").'

/** `v-model.number` num campo limpo devolve `''`: vazio é "sem prazo" (`null`), não 0. */
export function normalizeAuditDeadlineHours(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

/**
 * Par serializado para o backend (§6.1): sempre explícito — `null` limpa, ausente seria
 * "não mudou" e o auditor não conseguiria tirar o prazo. Sem horas, o cálculo vai `null`.
 */
export function buildAuditDeadlineSettings<T>(
  hours: number | null | undefined,
  calculationType: T | null | undefined
): { auditDeadlineHours: number | null; auditDeadlineCalculationType: T | null } {
  if (hours === null || hours === undefined) {
    return { auditDeadlineHours: null, auditDeadlineCalculationType: null }
  }
  return { auditDeadlineHours: hours, auditDeadlineCalculationType: calculationType ?? null }
}

/** Limiar de Atenção em minutos: menor entre 24 h e 25% da janela (Q12, G2-D2). */
export function auditAttentionMinutes(hours: number): number {
  return Math.min(24 * 60, hours * 15)
}

const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const
/** "seg 10:00", em minutos contados de dom 00:00 — o exemplo fixo do mockup. */
const MONDAY_10H = 1 * 1440 + 10 * 60

const pad = (n: number): string => String(n).padStart(2, '0')

function stamp(totalMinutes: number): string {
  const day = Math.floor(totalMinutes / 1440) % 7
  const minutes = totalMinutes % 1440
  return `${WEEKDAYS[day]} ${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`
}

/** Prévia do mockup `politicaregistro--padrao.png`; `null` quando não há o que exemplificar. */
export function buildAuditDeadlinePreview(hours: number | null): string | null {
  if (hours === null || !Number.isInteger(hours) || hours <= 0) return null
  if (hours > AUDIT_DEADLINE_MAX_HOURS) return null
  const attention = auditAttentionMinutes(hours)
  const overdueAt = MONDAY_10H + hours * 60
  const attentionAt = overdueAt - attention
  const attentionLabel = attention % 60 === 0 ? `${attention / 60} h` : `${attention} min`
  return `Exemplo com ${hours} h (em horas corridas): entrou em auditoria seg 10:00 → Atenção a partir de ${stamp(attentionAt)} (últimas ${attentionLabel}) → Vencida ${stamp(overdueAt)}. Horas úteis contam seg–sex no fuso da escala, sem feriados.`
}
